import { useEffect, useState } from 'react';
import type { WorkOrderDocumentTargets } from '@tcg/shared';
import { ApiClientError, documentsApi } from '../lib/api';
import { DocumentDropzone } from '../components/DocumentDropzone';
import { WorkOrderPicker } from '../components/WorkOrderPicker';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, PageHeader, Spinner } from '../components/ui';
import { IconUpload } from '../components/icons';

/**
 * Document upload tool. Look up a Corrigo work order, then drag-and-drop files
 * onto its required-document slots and/or a general zone. Documents bind to the
 * work order (by WorkOrderId), so this can run alongside quote submission — the
 * same WO number ties the docs and the quote together.
 */
export function Documents() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [woNumber, setWoNumber] = useState('');
  const [targets, setTargets] = useState<WorkOrderDocumentTargets | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    documentsApi
      .status()
      .then((s) => setConfigured(s.configured))
      .catch(() => setConfigured(false));
  }, []);

  // A Corrigo WO number can match >1 work order, so resolve via the picker first.
  const lookup = () => {
    if (!woNumber.trim()) return;
    setError(null);
    setTargets(null);
    setPicking(true);
  };

  const loadTargets = async (workOrderId?: number) => {
    setBusy(true);
    setError(null);
    try {
      setTargets(await documentsApi.getTargets(woNumber.trim(), workOrderId));
      setPicking(false);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load the work order.');
      setPicking(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconUpload className="h-5 w-5" />}
        title="Upload Documents"
        subtitle="Attach files to a Corrigo work order — required-document slots and general attachments."
      />

      {configured === false && (
        <Banner tone="warn">
          Corrigo is not configured, so documents can’t be uploaded yet. An admin must set the
          Corrigo credentials.
        </Banner>
      )}

      <Card>
        <CardHeader
          title="Find the work order"
          subtitle="Documents attach to the work order — the same WO id used for the quote."
        />
        <CardBody>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Field label="Work order number">
                <Input
                  value={woNumber}
                  onChange={(e) => setWoNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void lookup()}
                  placeholder="WO-100245"
                  disabled={configured === false}
                />
              </Field>
            </div>
            <Button
              onClick={() => lookup()}
              disabled={busy || picking || !woNumber.trim() || configured === false}
            >
              {busy || picking ? <Spinner /> : 'Look up'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {error && <Banner tone="error">{error}</Banner>}

      {picking && (
        <WorkOrderPicker
          woNumber={woNumber.trim()}
          search={() => documentsApi.searchWorkOrders(woNumber.trim())}
          onResolved={(workOrderId) => void loadTargets(workOrderId)}
          onReset={() => setPicking(false)}
        />
      )}

      {targets && (
        <>
          <Card>
            <CardHeader
              title={`Work order ${targets.woNumber}`}
              subtitle={targets.customerName ?? undefined}
              action={
                targets.canUpload ? (
                  <Badge tone="green">{targets.status}</Badge>
                ) : (
                  <Badge tone="red">{targets.status}</Badge>
                )
              }
            />
            <CardBody>
              <p className="text-sm text-slate-500">
                {targets.hasRequiredDocuments
                  ? `${targets.requiredDocuments.length} required document slot(s) plus a general zone.`
                  : 'No required-document slots — use the general zone for supporting documents.'}
              </p>
            </CardBody>
          </Card>

          <DocumentDropzone targets={targets} />
        </>
      )}
    </div>
  );
}
